import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";

export default function TemplateAlexandreSophieRSVP({ isOpen, onClose, wedding }) {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Form State
  const [attending, setAttending] = useState(null);
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [guests, setGuests] = useState([]);
  const [dietary, setDietary] = useState("");
  const [song, setSong] = useState("");

  // API State
  const [guestToken, setGuestToken] = useState(null);
  const [isLookupLoading, setIsLookupLoading] = useState(false);

  const [lookupMode, setLookupMode] = useState("email");
  const [phone, setPhone] = useState("");
  const [emailRequestToken, setEmailRequestToken] = useState(null);
  const [validationEmail, setValidationEmail] = useState("");
  const [pin, setPin] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const loadLookupAccess = async (lookupToken, pinValue = "") => {
    const config = pinValue ? { headers: { "X-RSVP-PIN": pinValue } } : undefined;
    const rsvp = await api.post(`/public/wedding/${wedding.slug}/rsvp/access`, { lookup_token: lookupToken }, config);
    if (rsvp.data.require_pin) {
      setLookupMode("pin");
      return false;
    }

    setLookupMode("email");
    const initialGuests = (rsvp.data.household_members || []).map((member) => ({
      id: member.id,
      name: member.name,
      isChild: false,
      attending: member.rsvp_status === "declined" ? "no" : "yes",
    }));
    setGuests(initialGuests);
    return true;
  };

  // Prevent background scrolling when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  
  const handlePhoneLookup = async (e) => {
    e?.preventDefault();
    if (!phone) return;
    setIsLookupLoading(true);
    try {
      const res = await api.post(`/public/wedding/${wedding.slug}/rsvp/lookup_phone`, { phone });
      if (res.data.found && res.data.request_token) {
        setEmailRequestToken(res.data.request_token);
        setLookupMode("request_email");
      } else if (res.data.found && res.data.email_update_available === false) {
        toast.error("Pour des raisons de sécurité, la mise à jour d’email par téléphone est temporairement désactivée.");
      } else {
        toast.error("Numéro de téléphone non trouvé");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setIsLookupLoading(false);
    }
  };

  const addGuestRequest = () => {
    setGuests([...guests, { name: "", isChild: false, isUnlisted: true }]);
  };

  const removeGuestRequest = (index) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  const handleRequestEmailValidation = async (e) => {
    e?.preventDefault();
    if (!validationEmail || !emailRequestToken) return;
    setIsLookupLoading(true);
    try {
      await api.post(`/public/wedding/${wedding.slug}/rsvp/request_email_validation`, {
        request_token: emailRequestToken,
        email: validationEmail,
      });
      toast.success("Demande envoyée ! Vous recevrez un email sous 24h.");
      setTimeout(() => handleClose(), 3000);
    } catch (e) {
      toast.error("Erreur lors de la demande");
    } finally {
      setIsLookupLoading(false);
    }
  };

  const handleLookup = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setIsLookupLoading(true);
    try {
      const res = await api.post(`/public/wedding/${wedding.slug}/rsvp/lookup`, { fullname, email });
      if (res.data.found && res.data.lookup_token) {
        setGuestToken(res.data.lookup_token);
        const accessGranted = await loadLookupAccess(res.data.lookup_token);
        if (accessGranted) {
          nextStep();
        }
      } else {
        setGuestToken("request_only");
        nextStep();
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setIsLookupLoading(false);
    }
  };

  const handlePinLookup = async (e) => {
    e?.preventDefault();
    if (!guestToken || !pin) return;
    setIsLookupLoading(true);
    try {
      const accessGranted = await loadLookupAccess(guestToken, pin);
      if (accessGranted) {
        nextStep();
      } else {
        toast.error("Code PIN incorrect");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setIsLookupLoading(false);
    }
  };

  const updateGuest = (index, field, value) => {
    const newGuests = [...guests];
    newGuests[index][field] = value;
    setGuests(newGuests);
  };

  const handleRequestInvitation = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post(`/public/wedding/${wedding.slug}/rsvp/request`, { fullname, email });
      setIsSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 4000);
    } catch (e) {
      toast.error("Erreur lors de la demande");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const status = attending === "yes" ? "confirmed" : "declined";
      const householdUpdates = guests.map(g => ({
          guest_id: g.id,
          status: g.attending === "yes" ? "confirmed" : "declined",
          plus_one_names: []
      }));
      const unlisted = guests.filter((g) => g.isUnlisted && g.name.trim()).map((g) => ({
        name: g.name,
        is_child: g.isChild,
      }));

      const config = pin ? { headers: { "X-RSVP-PIN": pin } } : undefined;
      await api.post(`/public/wedding/${wedding.slug}/rsvp/submit`, {
          lookup_token: guestToken,
          status: status,
          dietary: dietary,
          message: song, 
          household_members: householdUpdates.filter((guest) => guest.guest_id),
          unlisted_companions: unlisted.length ? unlisted : undefined,
      }, config);
      setIsSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 4000);
    } catch (e) {
      toast.error("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setCurrentStep(1);
      setAttending(null);
      setFullname("");
      setEmail("");
      setGuests([]);
      setDietary("");
      setSong("");
      setGuestToken(null);
      setEmailRequestToken(null);
      setLookupMode("email");
      setPin("");
      setIsSuccess(false);
    }, 500);
  };

  const fadeVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col font-sans text-[#1b1c1c] overflow-y-auto" data-lenis-prevent="true">
      {/* Background with mesh gradient */}
      <div className="fixed inset-0 z-0 bg-[#fcf9f8]" style={{
          backgroundImage: `
            radial-gradient(at 0% 0%, rgba(209, 148, 139, 0.15) 0px, transparent 60%),
            radial-gradient(at 100% 0%, rgba(132, 81, 74, 0.1) 0px, transparent 60%),
            radial-gradient(at 100% 100%, rgba(178, 176, 155, 0.15) 0px, transparent 60%),
            radial-gradient(at 0% 100%, rgba(209, 148, 139, 0.1) 0px, transparent 60%),
            radial-gradient(#d1948b10 1px, transparent 1px)
          `,
          backgroundSize: 'cover, cover, cover, cover, 40px 40px'
      }}></div>

      {/* Progress Bar (Subtle) */}
      <div className="fixed top-0 left-0 w-full h-1 bg-[#eae7e7] z-[60]">
        <motion.div 
          className="h-full bg-gradient-to-r from-[#84514a] via-[#d1948b] to-[#84514a]"
          style={{ backgroundSize: '200% auto' }}
          animate={{ 
            width: `${(currentStep / totalSteps) * 100}%`,
            backgroundPosition: ['0% center', '200% center']
          }}
          transition={{ 
            width: { duration: 0.5 },
            backgroundPosition: { duration: 3, repeat: Infinity, ease: "linear" }
          }}
        ></motion.div>
      </div>

      {/* Header */}
      <header className="w-full flex justify-between items-center px-6 md:px-16 py-8 z-50">
        <span className="font-serif text-2xl text-[#84514a] italic">Ceremind</span>
        <button onClick={handleClose} aria-label="Close" className="group flex items-center gap-2 text-[#524441] hover:text-[#84514a] transition-all">
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] opacity-0 group-hover:opacity-100 transition-opacity">Fermer</span>
          <span className="material-symbols-outlined text-[24px] transition-transform duration-300 group-hover:rotate-90">close</span>
        </button>
      </header>

      <main className="flex-grow flex flex-col items-center justify-center w-full px-6 max-w-5xl mx-auto relative z-10 py-12">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div key="step1" variants={fadeVariants} initial="hidden" animate="visible" exit="exit" className="w-full flex flex-col items-center">
              <div className="text-center mb-16">
                <span className="font-sans text-[11px] uppercase tracking-[0.5em] text-[#84514a] mb-6 block">Invitation Exclusive</span>
                <h1 className="font-serif text-5xl md:text-7xl text-[#1b1c1c] mb-6 tracking-tight">Répondez s'il vous plaît</h1>
                <p className="font-sans text-lg text-[#524441] italic max-w-xl mx-auto leading-relaxed opacity-80">
                  Dans l'attente de célébrer ce moment précieux à vos côtés, nous vous prions de bien vouloir confirmer votre présence.
                </p>
              </div>

              <h2 className="font-sans text-[12px] uppercase tracking-[0.4em] text-[#847371] mb-12">Étape 1 de 4 : Votre Présence</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl mb-16">
                <button 
                  onClick={() => { setAttending('yes'); nextStep(); }}
                  className="group relative flex flex-col items-center justify-center aspect-[4/3] md:aspect-square border border-[#84514a]/20 rounded-2xl p-8 overflow-hidden bg-white/40 backdrop-blur-md transition-all duration-500 hover:bg-white/80 hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_rgba(132,81,74,0.15)] hover:border-[#84514a]"
                >
                  <div className="absolute inset-0 bg-[#84514a]/[0.01] group-hover:bg-[#84514a]/[0.03] transition-colors duration-700"></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <span className="material-symbols-outlined text-[56px] text-[#84514a]/30 group-hover:text-[#84514a] mb-8 transition-all duration-700 font-light group-hover:scale-110">auto_awesome</span>
                    <h3 className="font-serif text-3xl mb-4 text-[#1b1c1c]">Avec Joie</h3>
                    <p className="font-sans text-[#524441]/70 text-xs tracking-[0.1em] uppercase">Je serai présent pour célébrer</p>
                  </div>
                </button>
                <button 
                  onClick={() => { setAttending('no'); nextStep(); }}
                  className="group relative flex flex-col items-center justify-center aspect-[4/3] md:aspect-square border border-[#d6c2bf]/40 rounded-2xl p-8 overflow-hidden bg-white/40 backdrop-blur-md transition-all duration-500 hover:bg-white/80 hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_rgba(132,81,74,0.1)] hover:border-[#847371]"
                >
                  <div className="absolute inset-0 bg-[#615f46]/[0.01] group-hover:bg-[#615f46]/[0.03] transition-colors duration-700"></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <span className="material-symbols-outlined text-[56px] text-[#524441]/20 group-hover:text-[#524441]/80 mb-8 transition-all duration-700 font-light group-hover:scale-110">favorite</span>
                    <h3 className="font-serif text-3xl mb-4 text-[#1b1c1c]">Avec Regret</h3>
                    <p className="font-sans text-[#524441]/70 text-xs tracking-[0.1em] uppercase">Je ne pourrai malheureusement pas être là</p>
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div key="step2" variants={fadeVariants} initial="hidden" animate="visible" exit="exit" className="w-full max-w-[500px]">
              <div className="flex justify-center gap-3 mb-12">
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#84514a] scale-150 transition-transform duration-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
              </div>
              
              
              <div className="text-center mb-12">
                <span className="font-sans text-[10px] text-[#84514a] uppercase tracking-[0.2em] mb-4 block">ÉTAPE 2 SUR 4</span>
                <h2 className="font-serif text-4xl text-[#1b1c1c]">Vos Coordonnées</h2>
              </div>

              {lookupMode === "email" && (
                <form onSubmit={handleLookup} className="space-y-10">
                  <div className="relative pt-6">
                    <input 
                      id="fullname" 
                      type="text" 
                      value={fullname}
                      onChange={(e) => setFullname(e.target.value)}
                      className="peer w-full border-0 border-b border-[#d6c2bf] bg-transparent pb-3 font-sans text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 transition-colors placeholder-transparent" 
                      placeholder="Nom Complet"
                    />
                    <label htmlFor="fullname" className="absolute left-0 top-6 text-[#524441] font-serif italic text-lg pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:font-sans peer-focus:font-bold peer-focus:not-italic peer-focus:text-[#84514a] peer-valid:-top-2 peer-valid:text-xs peer-valid:uppercase peer-valid:tracking-[0.15em] peer-valid:font-sans peer-valid:font-bold peer-valid:not-italic peer-valid:text-[#84514a]">
                      Nom (facultatif)
                    </label>
                  </div>
                  
                  <div className="relative pt-6">
                    <input 
                      id="email" 
                      type="email" 
                      required 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="peer w-full border-0 border-b border-[#d6c2bf] bg-transparent pb-3 font-sans text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 transition-colors placeholder-transparent" 
                      placeholder="Adresse Email"
                    />
                    <label htmlFor="email" className="absolute left-0 top-6 text-[#524441] font-serif italic text-lg pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:font-sans peer-focus:font-bold peer-focus:not-italic peer-focus:text-[#84514a] peer-valid:-top-2 peer-valid:text-xs peer-valid:uppercase peer-valid:tracking-[0.15em] peer-valid:font-sans peer-valid:font-bold peer-valid:not-italic peer-valid:text-[#84514a]">
                      Adresse Email
                    </label>
                  </div>

                  <div className="text-center">
                    <button type="button" onClick={() => setLookupMode("phone")} className="text-xs text-[#84514a] underline mt-4">Je n'ai pas renseigné mon email / Utiliser mon téléphone</button>
                  </div>

                  <div className="flex items-center justify-between pt-8">
                    <button type="button" onClick={prevStep} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                      <span className="material-symbols-outlined text-[16px]">west</span>
                      Retour
                    </button>
                    <button type="submit" disabled={isLookupLoading} className="bg-[#84514a] hover:bg-[#683a34] text-white font-sans text-[11px] tracking-widest px-10 py-4 rounded-full transition-all uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5">
                      {isLookupLoading ? "Recherche..." : "Suivant"}
                    </button>
                  </div>
                </form>
              )}

              {lookupMode === "phone" && (
                <form onSubmit={handlePhoneLookup} className="space-y-10">
                  <div className="relative pt-6">
                    <input 
                      id="phone" 
                      type="tel" 
                      required 
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="peer w-full border-0 border-b border-[#d6c2bf] bg-transparent pb-3 font-sans text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 transition-colors placeholder-transparent" 
                      placeholder="Numéro de téléphone"
                    />
                    <label htmlFor="phone" className="absolute left-0 top-6 text-[#524441] font-serif italic text-lg pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:font-sans peer-focus:font-bold peer-focus:not-italic peer-focus:text-[#84514a] peer-valid:-top-2 peer-valid:text-xs peer-valid:uppercase peer-valid:tracking-[0.15em] peer-valid:font-sans peer-valid:font-bold peer-valid:not-italic peer-valid:text-[#84514a]">
                      Numéro de téléphone
                    </label>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" onClick={() => setLookupMode("email")} className="text-xs text-[#84514a] underline mt-4">Retour à la recherche par email</button>
                  </div>

                  <div className="flex items-center justify-between pt-8">
                    <button type="button" onClick={prevStep} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                      <span className="material-symbols-outlined text-[16px]">west</span>
                      Retour
                    </button>
                    <button type="submit" disabled={isLookupLoading} className="bg-[#84514a] hover:bg-[#683a34] text-white font-sans text-[11px] tracking-widest px-10 py-4 rounded-full transition-all uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5">
                      {isLookupLoading ? "Recherche..." : "Suivant"}
                    </button>
                  </div>
                </form>
              )}

              {lookupMode === "request_email" && (
                <form onSubmit={handleRequestEmailValidation} className="space-y-10">
                  <div className="text-center">
                    <p className="text-sm mb-4">Afin de sécuriser votre accès, veuillez renseigner votre email. Les mariés valideront votre accès et vous serez prévenu.</p>
                  </div>
                  <div className="relative pt-6">
                    <input 
                      id="validationEmail" 
                      type="email" 
                      required 
                      value={validationEmail}
                      onChange={(e) => setValidationEmail(e.target.value)}
                      className="peer w-full border-0 border-b border-[#d6c2bf] bg-transparent pb-3 font-sans text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 transition-colors placeholder-transparent" 
                      placeholder="Votre adresse email"
                    />
                    <label htmlFor="validationEmail" className="absolute left-0 top-6 text-[#524441] font-serif italic text-lg pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:font-sans peer-focus:font-bold peer-focus:not-italic peer-focus:text-[#84514a] peer-valid:-top-2 peer-valid:text-xs peer-valid:uppercase peer-valid:tracking-[0.15em] peer-valid:font-sans peer-valid:font-bold peer-valid:not-italic peer-valid:text-[#84514a]">
                      Votre adresse email
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between pt-8">
                    <button type="button" onClick={() => setLookupMode("phone")} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                      <span className="material-symbols-outlined text-[16px]">west</span>
                      Retour
                    </button>
                    <button type="submit" disabled={isLookupLoading} className="bg-[#84514a] hover:bg-[#683a34] text-white font-sans text-[11px] tracking-widest px-10 py-4 rounded-full transition-all uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5">
                      {isLookupLoading ? "Envoi..." : "Envoyer"}
                    </button>
                  </div>
                </form>
              )}

              {lookupMode === "pin" && (
                <form onSubmit={handlePinLookup} className="space-y-10">
                  <div className="text-center">
                    <p className="text-sm mb-4">Cette invitation est protégée. Saisissez le code PIN indiqué sur votre faire-part.</p>
                  </div>
                  <div className="relative pt-6">
                    <input
                      id="lookupPin"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      required
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      className="peer w-full border-0 border-b border-[#d6c2bf] bg-transparent pb-3 font-sans text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 transition-colors placeholder-transparent"
                      placeholder="Code PIN"
                    />
                    <label htmlFor="lookupPin" className="absolute left-0 top-6 text-[#524441] font-serif italic text-lg pointer-events-none transition-all duration-300 peer-focus:-top-2 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.15em] peer-focus:font-sans peer-focus:font-bold peer-focus:not-italic peer-focus:text-[#84514a] peer-valid:-top-2 peer-valid:text-xs peer-valid:uppercase peer-valid:tracking-[0.15em] peer-valid:font-sans peer-valid:font-bold peer-valid:not-italic peer-valid:text-[#84514a]">
                      Code PIN
                    </label>
                  </div>

                  <div className="flex items-center justify-between pt-8">
                    <button type="button" onClick={() => setLookupMode("email")} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                      <span className="material-symbols-outlined text-[16px]">west</span>
                      Retour
                    </button>
                    <button type="submit" disabled={isLookupLoading} className="bg-[#84514a] hover:bg-[#683a34] text-white font-sans text-[11px] tracking-widest px-10 py-4 rounded-full transition-all uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5">
                      {isLookupLoading ? "Vérification..." : "Accéder"}
                    </button>
                  </div>
                </form>
              )}

            </motion.div>
          )}

          {currentStep === 3 && guestToken === "request_only" && (
            <motion.div key="step3_notfound" variants={fadeVariants} initial="hidden" animate="visible" exit="exit" className="w-full max-w-[500px]">
              <div className="text-center mb-12">
                <div className="mx-auto w-16 h-16 bg-[#ba1a1a]/10 rounded-full flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-3xl text-[#ba1a1a]">search_off</span>
                </div>
                <h2 className="font-serif text-3xl text-[#1b1c1c] mb-4">Personne non trouvée</h2>
                <p className="font-sans text-[#524441] leading-relaxed">
                  Nous n'avons pas trouvé de nom correspondant sur la liste officielle. Souhaitez-vous envoyer une demande de validation aux mariés ?
                </p>
              </div>

              <div className="flex items-center justify-between pt-8">
                <button type="button" onClick={prevStep} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                  <span className="material-symbols-outlined text-[16px]">west</span>
                  Retour
                </button>
                <button type="button" onClick={handleRequestInvitation} disabled={isSubmitting} className="bg-[#1b1c1c] hover:bg-[#303030] text-white font-sans text-[11px] tracking-widest px-8 py-4 rounded-full transition-all uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5">
                  Demander
                </button>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && guestToken !== "request_only" && (
            <motion.div key="step3" variants={fadeVariants} initial="hidden" animate="visible" exit="exit" className="w-full max-w-[600px]">
              <div className="flex justify-center gap-3 mb-10">
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#84514a] scale-150 transition-transform duration-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
              </div>
              
              <div className="text-center mb-10">
                <span className="font-sans text-[10px] text-[#84514a] uppercase tracking-[0.2em] mb-4 block">ÉTAPE 3 SUR 4</span>
                <h2 className="font-serif text-3xl text-[#1b1c1c] mb-2">Membres de votre famille</h2>
                <p className="font-sans text-sm text-[#524441]">Ajoutez ou modifiez les personnes qui vous accompagneront.</p>
              </div>

              <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2" data-lenis-prevent="true">
                {guests.length === 0 && (
                  <div className="text-center p-8 bg-white/40 border border-[#d6c2bf]/50 rounded-xl">
                    <p className="font-sans text-sm text-[#524441]">Vous êtes le seul invité enregistré sous ce nom.</p>
                  </div>
                )}
                {guests.map((guest, index) => (
                  <div key={index} className="bg-white/60 backdrop-blur-sm border border-[#d6c2bf] rounded-xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all duration-300 hover:shadow-sm">
                    <div className="flex-grow w-full">
                      <input 
                        type="text" 
                        value={guest.name}
                        onChange={(e) => updateGuest(index, 'name', e.target.value)}
                        className="w-full border-none border-b border-[#d6c2bf] bg-transparent pb-1 font-serif text-lg text-[#1b1c1c] focus:border-[#84514a] focus:ring-0 px-0 disabled:opacity-70 disabled:border-transparent" 
                        placeholder="Nom de l'accompagnant" 
                        disabled={!guest.isUnlisted}
                      />
                      {guest.isUnlisted ? (
                        <div className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[#84514a]">Demande à valider par les mariés</div>
                      ) : null}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                      {!guest.isUnlisted ? <div className="flex bg-[#f6f3f2] rounded-lg p-1 border border-[#e5e2dd]">
                        <button
                          type="button"
                          onClick={() => updateGuest(index, 'attending', 'yes')}
                          className={`px-4 py-1.5 text-[10px] uppercase font-bold tracking-widest rounded-md transition-colors ${guest.attending === 'yes' ? 'bg-[#84514a] text-white shadow-sm' : 'text-[#524441] hover:bg-white'}`}
                        >
                          Présent
                        </button>
                        <button
                          type="button"
                          onClick={() => updateGuest(index, 'attending', 'no')}
                          className={`px-4 py-1.5 text-[10px] uppercase font-bold tracking-widest rounded-md transition-colors ${guest.attending === 'no' ? 'bg-[#1b1c1c] text-white shadow-sm' : 'text-[#524441] hover:bg-white'}`}
                        >
                          Absent
                        </button>
                      </div> : null}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={guest.isChild}
                          onChange={(e) => updateGuest(index, 'isChild', e.target.checked)}
                          className="rounded-sm border-[#847371] text-[#84514a] focus:ring-[#84514a]" 
                        />
                        <span className="font-sans text-[10px] uppercase tracking-widest text-[#524441]">Enfant</span>
                      </label>
                      {guest.isUnlisted ? (
                        <button type="button" onClick={() => removeGuestRequest(index)} className="p-1 text-[#847371] hover:text-[#ba1a1a] transition-colors" aria-label="Retirer la demande d’accompagnant">×</button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <button type="button" onClick={addGuestRequest} className="rounded-full border border-[#d1948b]/30 bg-white/50 px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-[#84514a] transition-colors hover:border-[#84514a]">
                  Demander un accompagnant
                </button>
              </div>

              <div className="flex items-center justify-between pt-10">
                <button type="button" onClick={prevStep} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1">
                  <span className="material-symbols-outlined text-[16px]">west</span>
                  Retour
                </button>
                <button type="button" onClick={nextStep} className="bg-[#84514a] hover:bg-[#683a34] text-white font-sans text-[11px] tracking-widest px-10 py-4 rounded-full transition-all uppercase hover:shadow-lg hover:-translate-y-0.5">
                  Suivant
                </button>
              </div>
            </motion.div>
          )}

          {currentStep === 4 && (
            <motion.div key="step4" variants={fadeVariants} initial="hidden" animate="visible" exit="exit" className="w-full max-w-[500px]">
              <div className="flex justify-center gap-3 mb-10">
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#d6c2bf]"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#84514a] scale-150 transition-transform duration-300"></div>
              </div>

              <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-[0_12px_32px_rgba(132,81,74,0.08)] border border-white/50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#84514a]/5 rounded-bl-full -mr-12 -mt-12"></div>
                
                <h2 className="font-serif text-3xl text-[#1b1c1c] mb-10 text-center">Détails Finaux</h2>
                
                <form onSubmit={handleSubmit} className="space-y-10">
                  <div className="relative group">
                    <label className="font-sans text-[10px] uppercase tracking-widest text-[#524441] block mb-2 transition-colors group-focus-within:text-[#84514a]">
                      Restrictions Alimentaires (Optionnel)
                    </label>
                    <textarea 
                      value={dietary}
                      onChange={(e) => setDietary(e.target.value)}
                      className="w-full border-0 border-b border-[#d6c2bf] bg-transparent py-3 font-sans text-[#1b1c1c] resize-none focus:border-[#84514a] focus:ring-0 transition-colors" 
                      placeholder="Allergies, régimes spécifiques..." 
                      rows="2"
                    ></textarea>
                  </div>

                  <div className="relative group">
                    <label className="font-sans text-[10px] uppercase tracking-widest text-[#524441] block mb-2 transition-colors group-focus-within:text-[#84514a]">
                      Une Chanson pour Danser ?
                    </label>
                    <textarea 
                      value={song}
                      onChange={(e) => setSong(e.target.value)}
                      className="w-full border-0 border-b border-[#d6c2bf] bg-transparent py-3 font-sans text-[#1b1c1c] resize-none focus:border-[#84514a] focus:ring-0 transition-colors" 
                      placeholder="Le titre qui vous fera rejoindre la piste..." 
                      rows="2"
                    ></textarea>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-between pt-6 gap-6">
                    <button type="button" onClick={prevStep} className="group flex items-center gap-2 font-sans text-[11px] tracking-widest text-[#524441]/80 hover:text-[#84514a] uppercase transition-all hover:-translate-x-1 w-full sm:w-auto justify-center">
                      <span className="material-symbols-outlined text-[16px]">west</span>
                      Retour
                    </button>
                    <button 
                      type="submit" 
                      disabled={isSubmitting} 
                      className="relative overflow-hidden bg-gradient-to-br from-[#84514a] via-[#a6746d] to-[#84514a] text-white font-sans text-[11px] tracking-widest px-8 py-5 rounded-lg transition-all duration-500 uppercase disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5 w-full sm:w-auto text-center"
                      style={{ backgroundSize: '200% auto' }}
                    >
                      {isSubmitting ? "Envoi..." : (guests.some((guest) => guest.isUnlisted) ? "Confirmer et envoyer la demande" : "Confirmer ma présence")}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Success Modal */}
      <AnimatePresence>
        {isSuccess && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-[#fcf9f8]/95 backdrop-blur-xl z-[200] flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }} 
              className="text-center max-w-md"
            >
              <div className="mb-8 flex justify-center">
                <span className="material-symbols-outlined text-[#84514a] text-[80px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
              </div>
              <h2 className="font-serif text-5xl text-[#1b1c1c] mb-6">Merci !</h2>
              <p className="font-sans text-lg text-[#524441] mb-10 leading-relaxed">
                Votre réponse a été enregistrée avec succès. Nous avons hâte de célébrer ce moment avec vous.
              </p>
              <button onClick={handleClose} className="font-sans text-[11px] uppercase tracking-widest text-[#84514a] border border-[#84514a] px-10 py-4 rounded-full hover:bg-[#84514a] hover:text-white transition-all duration-300">
                Fermer
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="w-full py-8 border-t border-[#d6c2bf]/30 bg-white/20 backdrop-blur-sm z-50">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center px-6 gap-4">
          <p className="font-sans text-[10px] text-[#847371] uppercase tracking-[0.2em]">© 2024 Ceremind Wedding Experience</p>
          <div className="flex gap-6">
            <button type="button" className="font-sans text-[10px] text-[#847371] hover:text-[#84514a] uppercase tracking-[0.2em] transition-colors">Aide</button>
            <button type="button" className="font-sans text-[10px] text-[#847371] hover:text-[#84514a] uppercase tracking-[0.2em] transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
